/**
 * QRISE Mystery Box — animated reveal component (v2, natural physics).
 *
 * Three states, driven by the parent:
 *   - "idle"     → box closed, gentle floating + subtle 3D rotation
 *   - "opening"  → 3-phase opening:
 *                   1) anticipation: tiny shake forward
 *                   2) lift + crack open along back hinge
 *                   3) tilt back to ~115°, slight settle bounce
 *                  + confetti burst, light beam, halo, prize float
 *   - "shaking"  → box trembles 3 times, lid stays sealed,
 *                   "REFUSÉ" stamp drops in (loser)
 *
 * Key fix vs v1: the lid now rotates around the BACK HINGE of the body
 * (transformOrigin: 50% 44% — at the top of the body sprite, where the
 * lid actually meets the body). Combined with `rotateX` and CSS
 * `perspective` on the wrapper, the opening reads as a real 3D tilt
 * backwards instead of a flat 2D flip around its centre.
 */
import { motion, AnimatePresence } from "framer-motion";
import { useMemo } from "react";
import {
  getBoxStyleBody,
  getBoxStyleLid,
  getBoxStyleAccent,
} from "./MysteryBoxStyles";

const CONFETTI_COLORS = ["#fbbf24", "#f59e0b", "#fde047", "#ffffff", "#ec4899", "#a78bfa", "#34d399"];

export default function MysteryBoxAnimation({ styleKey, state, prizeLabel, prizeColor, prizeImage = null }) {
  const bodySrc = getBoxStyleBody(styleKey || "gold_gift");
  const lidSrc = getBoxStyleLid(styleKey || "gold_gift");
  const accent = getBoxStyleAccent(styleKey || "gold_gift");

  // Confetti — generated once. Each piece has its own angle, distance,
  // delay, rotation, size and shape so the burst looks natural rather
  // than mechanical. Bias the angle upward so most pieces shoot up out
  // of the box, with some lateral spread.
  const confetti = useMemo(
    () =>
      Array.from({ length: 22 }).map(() => {
        const angle = -90 + (Math.random() - 0.5) * 130; // mostly up
        const distance = 110 + Math.random() * 130;
        return {
          color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
          x: Math.cos((angle * Math.PI) / 180) * distance,
          y: Math.sin((angle * Math.PI) / 180) * distance,
          delay: Math.random() * 0.25,
          rotate: (Math.random() - 0.5) * 720,
          size: 5 + Math.random() * 7,
          shape: Math.random() > 0.55 ? "rect" : "circle",
        };
      }),
    [],
  );

  // Lid: 3-phase opening — anticipation, crack, full open (kept under 90°
  // so the lid stays visible like a real treasure chest tipped open).
  //   t=0.00  → closed
  //   t=0.18  → tiny anticipation forward (-6°) + lift (-2px)
  //   t=0.34  → crack: lifted, hinging slightly back (24°)
  //   t=0.85  → over-shoots open at 92°
  //   t=1.00  → settles back to a natural 78°
  const lidVariants = {
    idle: { rotateX: 0, y: 0, transition: { duration: 0.4 } },
    opening: {
      rotateX: [0, -6, 24, 92, 78],
      y: [0, -2, -10, -28, -24],
      transition: {
        duration: 1.05,
        times: [0, 0.18, 0.34, 0.85, 1],
        ease: ["easeOut", "easeIn", "easeOut", "easeOut"],
      },
    },
    shaking: {
      rotateX: 0,
      y: 0,
      x: [0, -3, 3, -2, 2, 0],
      transition: { duration: 0.5, repeat: 2, ease: "easeInOut" },
    },
  };

  // Body: idle floats and gently rotates around Y; opening adds a tiny
  // squash bounce to feel rooted; shaking shakes harder than the lid.
  const bodyVariants = {
    idle: {
      y: [0, -5, 0],
      rotateY: [0, -3, 0, 3, 0],
      transition: { duration: 4.6, repeat: Infinity, ease: "easeInOut" },
    },
    opening: {
      y: [0, 2, 0, -3, 0],
      scaleY: [1, 0.97, 1, 1.02, 1],
      transition: { duration: 0.95, ease: "easeOut" },
    },
    shaking: {
      x: [0, -10, 10, -8, 8, -4, 4, 0],
      y: [0, -1, 1, -1, 1, 0],
      transition: { duration: 0.55, repeat: 2 },
    },
  };

  return (
    <div
      className="relative mx-auto"
      style={{
        width: "min(78vw, 360px)",
        aspectRatio: "1 / 1",
        perspective: 1200,
        perspectiveOrigin: "50% 60%",
      }}
      data-testid="mystery-box-animation"
    >
      {/* Soft ground shadow that grows when opening */}
      <motion.div
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          bottom: "8%",
          width: "62%",
          height: "10%",
          background: "radial-gradient(ellipse, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 70%)",
          filter: "blur(4px)",
          zIndex: 0,
        }}
        animate={
          state === "opening"
            ? { scaleX: [1, 1.15, 1.08], opacity: [0.6, 0.8, 0.7] }
            : { scaleX: 1, opacity: 0.55 }
        }
        transition={{ duration: 0.9 }}
      />

      {/* Halo glow — winner only */}
      <AnimatePresence>
        {state === "opening" && (
          <motion.div
            className="absolute inset-0 -m-12 pointer-events-none"
            initial={{ opacity: 0, scale: 0.55 }}
            animate={{ opacity: [0, 0.95, 0.7], scale: [0.6, 1.5, 1.35] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, times: [0, 0.6, 1] }}
            style={{
              background: `radial-gradient(circle at 50% 42%, ${accent}77 0%, ${accent}22 38%, transparent 72%)`,
              filter: "blur(10px)",
              zIndex: 0,
            }}
          />
        )}
      </AnimatePresence>

      {/* Light beam shooting up from inside the box */}
      <AnimatePresence>
        {state === "opening" && (
          <motion.div
            className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
            style={{
              top: "-12%",
              width: "70%",
              height: "70%",
              background: `linear-gradient(180deg, transparent 0%, ${accent}aa 45%, ${accent}55 75%, transparent 100%)`,
              clipPath: "polygon(46% 0%, 54% 0%, 86% 100%, 14% 100%)",
              transformOrigin: "50% 100%",
              filter: "blur(2px)",
              mixBlendMode: "screen",
              zIndex: 1,
            }}
            initial={{ opacity: 0, scaleY: 0.1 }}
            animate={{ opacity: [0, 0.95, 0.85], scaleY: [0.1, 1.1, 1] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.85, delay: 0.32, times: [0, 0.7, 1] }}
          />
        )}
      </AnimatePresence>

      {/* Inside-of-box dark interior — appears at the top of the body
          right when the lid cracks open. Sits BEHIND the lid so the
          rising lid uncovers it naturally. */}
      <AnimatePresence>
        {state === "opening" && (
          <motion.div
            className="absolute pointer-events-none"
            style={{
              left: "16%",
              right: "16%",
              top: "38%",
              height: "12%",
              borderRadius: "4px 4px 8px 8px",
              background: `radial-gradient(ellipse at 50% 0%, ${accent} 0%, #1a0a2e 55%, #050108 100%)`,
              boxShadow: `inset 0 6px 18px rgba(0,0,0,0.85), 0 0 30px ${accent}66`,
              zIndex: 3,
            }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, delay: 0.28 }}
          />
        )}
      </AnimatePresence>

      {/* Body */}
      <motion.img
        src={bodySrc}
        alt=""
        className="absolute inset-0 w-full h-full pointer-events-none select-none"
        style={{
          zIndex: 2,
          filter: "drop-shadow(0 14px 18px rgba(0,0,0,0.45))",
          transformStyle: "preserve-3d",
        }}
        variants={bodyVariants}
        animate={state}
        initial="idle"
      />

      {/* Lid — pivots around the back hinge (50% 44% of the SVG: top of
          the body in the 200×200 viewBox where the lid sits). */}
      <motion.img
        src={lidSrc}
        alt=""
        className="absolute inset-0 w-full h-full pointer-events-none select-none"
        style={{
          zIndex: 5,
          transformOrigin: "50% 44%",
          transformStyle: "preserve-3d",
          filter: state === "opening"
            ? `drop-shadow(0 18px 22px rgba(0,0,0,0.55)) drop-shadow(0 0 12px ${accent}66)`
            : "drop-shadow(0 10px 14px rgba(0,0,0,0.4))",
        }}
        variants={lidVariants}
        animate={state}
        initial="idle"
      />

      {/* Confetti burst — fired right after the lid clears */}
      <AnimatePresence>
        {state === "opening" && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: "50%",
              top: "44%",
              width: 0,
              height: 0,
              zIndex: 8,
            }}
          >
            {confetti.map((c, i) => (
              <motion.div
                key={i}
                initial={{ x: 0, y: 0, opacity: 0, rotate: 0, scale: 0 }}
                animate={{
                  x: c.x,
                  y: c.y,
                  opacity: [0, 1, 1, 0],
                  rotate: c.rotate,
                  scale: [0, 1.05, 1, 0.9],
                }}
                transition={{
                  duration: 1.35,
                  delay: 0.42 + c.delay,
                  ease: "easeOut",
                  opacity: { duration: 1.35, times: [0, 0.12, 0.7, 1], delay: 0.42 + c.delay },
                  scale: { duration: 1.35, times: [0, 0.2, 0.55, 1], delay: 0.42 + c.delay },
                }}
                style={{
                  position: "absolute",
                  left: -c.size / 2,
                  top: -c.size / 2,
                  width: c.size,
                  height: c.shape === "rect" ? c.size * 0.42 : c.size,
                  borderRadius: c.shape === "rect" ? 1 : "50%",
                  background: c.color,
                  boxShadow: `0 0 8px ${c.color}aa`,
                }}
              />
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Sparkles around the prize */}
      <AnimatePresence>
        {state === "opening" && prizeLabel && (
          <motion.div
            className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
            style={{ top: "16%", zIndex: 9 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.55 }}
          >
            {[0, 60, 120, 180, 240, 300].map((deg, i) => (
              <motion.span
                key={deg}
                initial={{ scale: 0, opacity: 0 }}
                animate={{
                  scale: [0, 1.1, 0.8, 1],
                  opacity: [0, 1, 0.85, 1],
                }}
                transition={{
                  duration: 1.4,
                  delay: 0.6 + i * 0.07,
                  repeat: Infinity,
                  repeatDelay: 1.6,
                  ease: "easeOut",
                }}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: 6,
                  height: 6,
                  background: "white",
                  borderRadius: "50%",
                  boxShadow: `0 0 10px white, 0 0 16px ${accent}`,
                  transform: `rotate(${deg}deg) translate(0, -42px)`,
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Prize reveal floating up out of the open box */}
      <AnimatePresence>
        {state === "opening" && prizeLabel && (
          <motion.div
            className="absolute left-1/2"
            style={{ top: prizeImage ? "-2%" : "16%", zIndex: 10 }}
            initial={{ opacity: 0, x: "-50%", y: 60, scale: 0.55 }}
            animate={{ opacity: 1, x: "-50%", y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.55, ease: [0.34, 1.6, 0.64, 1] }}
            data-testid="mystery-prize-label"
          >
            <div className="flex flex-col items-center gap-2">
              {prizeImage && (
                <img
                  src={prizeImage}
                  alt=""
                  draggable={false}
                  className="h-24 w-24 object-contain sm:h-28 sm:w-28"
                  style={{ filter: "drop-shadow(0 16px 20px rgba(0,0,0,0.5))" }}
                  data-testid="mystery-prize-image"
                />
              )}
              <div
                className="rounded-2xl px-6 py-3 font-black text-base sm:text-lg whitespace-nowrap"
                style={{
                  background: `linear-gradient(135deg, ${prizeColor || accent} 0%, ${accent} 100%)`,
                  color: "#0b0418",
                  boxShadow: `0 22px 60px -12px ${accent}cc, inset 0 1px 0 rgba(255,255,255,0.6)`,
                  border: "1px solid rgba(255,255,255,0.4)",
                  textShadow: "0 1px 0 rgba(255,255,255,0.45)",
                }}
              >
                {prizeLabel}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loser stamp — overlay on the box when shaking */}
      <AnimatePresence>
        {state === "shaking" && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
            initial={{ opacity: 0, scale: 1.8, rotate: -22 }}
            animate={{ opacity: 1, scale: 1, rotate: -12 }}
            transition={{ duration: 0.45, delay: 0.6, ease: [0.34, 1.5, 0.64, 1] }}
            data-testid="mystery-loser-stamp"
          >
            <div
              className="rounded-2xl border-[3px] px-5 py-2 backdrop-blur-sm"
              style={{
                borderColor: "#ef4444",
                background: "rgba(239,68,68,0.12)",
                boxShadow: "0 0 0 4px rgba(239,68,68,0.15), inset 0 0 0 1px rgba(239,68,68,0.5)",
              }}
            >
              <div className="text-red-500 font-black text-base sm:text-lg tracking-[0.42em] uppercase">
                ✕ Refusé
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
